import type { CSSProperties, ReactNode } from 'react'
import { useEffect, useId, useRef, type RefObject } from 'react'
import { createPortal } from 'react-dom'
import { cn } from '../lib/cn'
import { useFocusTrap, useLockBodyScroll } from '../lib/hooks'
import { Button } from './Button'
import { IconClose } from './icons'
import {
  type OverlayPhase,
  overlayTakesInput,
  useInitialFocus,
  useOverlayEscape,
  useOverlayPresence,
} from './overlay-layer'

export type DrawerWidth = 'xs' | 'sm' | 'md' | 'lg'

/*
 * The floating sheet's three measured widths.
 *
 *   sm 400  Keyboard shortcuts
 *   md 540  Contact, read-only inside a campaign and when editing
 *   lg 680  Message editor
 *
 * `xs` was 390 for the shortcuts and is now the same 400 as `sm`. It is kept
 * only so the call sites that ask for it still compile. New code asks for `sm`.
 *
 * Below 640px the sheet is the window less its 8px inset on each side, which
 * is `w-full` inside the root's padding.
 */
const WIDTHS: Record<DrawerWidth, string> = {
  xs: 'max-w-[25rem]',
  sm: 'max-w-[25rem]',
  md: 'max-w-[33.75rem]',
  lg: 'max-w-[42.5rem]',
}

/**
 * How long the sheet takes to leave, which is how long it takes to arrive:
 * Vaul's 500ms on Vaul's curve, played backwards.
 */
export const DRAWER_EXIT_MS = 500
const SHEET_EASE = 'cubic-bezier(0.32, 0.72, 0, 1)'

export interface DrawerProps {
  open: boolean
  onClose: () => void
  title: ReactNode
  description?: ReactNode
  children: ReactNode
  /** Buttons for the right of the footer. */
  footer?: ReactNode
  /**
   * The footer's left end, for the destructive action (`Delete contact` left,
   * `Save changes and close` right).
   */
  footerStart?: ReactNode
  width?: DrawerWidth
  dismissible?: boolean
  className?: string
  /**
   * Whether the page behind is shut off while the panel is open. Defaults to
   * true, which every list screen wants.
   *
   * False leaves no backdrop, no scroll lock and no focus trap, so whatever the
   * panel is about stays visible and usable beside it. The canvas uses it for a
   * block's settings: every change applies straight away, and a dimmed drawing
   * behind a modal was why a change looked unconfirmed on 2026-09-13.
   */
  modal?: boolean
  /** A line above the footer, read out when it changes. */
  status?: ReactNode
  /**
   * A chip between the title and the close, such as `Saved`. Unlike `status`
   * it is not a live region: the autosave chip announces itself.
   */
  headerStatus?: ReactNode
  /**
   * The drawer's own header in place of the title row, for the message editor
   * and the contact. The title is still rendered for screen readers, because
   * it is what names the dialog. A custom header brings its own close.
   */
  header?: ReactNode
  /**
   * Where keyboard focus goes back to when a non-modal panel closes, such as
   * the control that opened it. A modal panel restores focus on its own.
   */
  returnFocus?: () => HTMLElement | null
}

/**
 * A floating side sheet for detail that should not lose the list behind it.
 * Use it for inspecting one row while the table stays in view.
 *
 * It is opened by a search param or a route, never by state alone, so a link
 * reproduces it and Back closes it. `useOverlayParam` in `lib/url-state.ts`
 * is the hook that wires `open` and `onClose` to the URL.
 */
export function Drawer({
  open,
  onClose,
  title,
  description,
  children,
  footer,
  footerStart,
  width = 'md',
  dismissible = true,
  className,
  modal = true,
  status,
  headerStatus,
  header,
  returnFocus,
}: DrawerProps) {
  const titleId = useId()
  const descriptionId = useId()
  const phase = useOverlayPresence(open, DRAWER_EXIT_MS)
  const live = overlayTakesInput(phase)
  const containerRef = useFocusTrap<HTMLDivElement>(live && modal)
  useInitialFocus(live && modal, containerRef)
  useLockBodyScroll(phase !== 'closed' && modal)
  useOverlayEscape(live && dismissible, onClose)
  // `open` and not `live`: the two are the same boolean (the phase is open
  // exactly while `open` is true), and `canvas-wiring.test.ts` pins this line.
  useNonModalFocus(open && !modal, containerRef, returnFocus)

  if (phase === 'closed') return null

  return createPortal(
    <DrawerFrame
      phase={phase}
      onClose={onClose}
      title={title}
      description={description}
      footer={footer}
      footerStart={footerStart}
      width={width}
      dismissible={dismissible}
      className={className}
      modal={modal}
      status={status}
      headerStatus={headerStatus}
      header={header}
      titleId={titleId}
      descriptionId={descriptionId}
      containerRef={containerRef}
    >
      {children}
    </DrawerFrame>,
    document.body,
  )
}

// ---------------------------------------------------------------------------
// The painted half
// ---------------------------------------------------------------------------

export interface DrawerFrameProps extends Omit<DrawerProps, 'open' | 'returnFocus'> {
  phase: OverlayPhase
  titleId: string
  descriptionId: string
  containerRef?: RefObject<HTMLDivElement>
}

/**
 * The sheet runs its arrival backwards on the way out. Inline, because these
 * change the moment closing starts. The scrim fades on the same curve. The
 * panel only ever moves, never fades, so a timeline that does not run (a
 * background tab) cannot leave it half visible.
 */
export function drawerExitStyles(phase: OverlayPhase): { scrim?: CSSProperties; panel?: CSSProperties } {
  if (phase !== 'closing') return {}
  return {
    scrim: { opacity: 0, transition: `opacity ${DRAWER_EXIT_MS}ms ${SHEET_EASE}` },
    panel: {
      transform: 'translateX(calc(100% + 8px))',
      transition: `transform ${DRAWER_EXIT_MS}ms ${SHEET_EASE}`,
    },
  }
}

/** The drawer without its portal or its hooks, so it can be rendered on its own. */
export function DrawerFrame({
  phase,
  onClose,
  title,
  description,
  children,
  footer,
  footerStart,
  width = 'md',
  dismissible = true,
  className,
  modal = true,
  status,
  headerStatus,
  header,
  titleId,
  descriptionId,
  containerRef,
}: DrawerFrameProps) {
  const live = overlayTakesInput(phase)
  const exit = drawerExitStyles(phase)

  return (
    <div
      data-overlay="drawer"
      data-phase={phase}
      aria-hidden={live ? undefined : true}
      className={cn(
        'fixed inset-0 z-modal flex justify-end p-2',
        // A non-modal sheet never takes the page's clicks, and a closing one
        // stops taking them the moment it starts to go. See overlay-layer.ts.
        (!modal || !live) && 'pointer-events-none',
      )}
    >
      {modal ? (
        <div
          data-overlay-scrim=""
          className="absolute inset-0 animate-sheet-scrim-in bg-black/70"
          style={exit.scrim}
          onClick={live && dismissible ? onClose : undefined}
          aria-hidden="true"
        />
      ) : null}
      <div
        ref={containerRef}
        role="dialog"
        aria-modal={modal ? 'true' : 'false'}
        aria-labelledby={titleId}
        aria-describedby={description ? descriptionId : undefined}
        tabIndex={-1}
        style={exit.panel}
        className={cn(
          'relative flex h-full w-full animate-sheet-in flex-col overflow-hidden rounded-xl bg-surface shadow-lg',
          !modal && live && 'pointer-events-auto',
          WIDTHS[width],
          className,
        )}
      >
        {header !== undefined ? (
          <>
            <h2 id={titleId} className="sr-only">
              {title}
            </h2>
            {description ? (
              <p id={descriptionId} className="sr-only">
                {description}
              </p>
            ) : null}
            <div className="shrink-0">{header}</div>
          </>
        ) : (
          <div className="flex shrink-0 items-start gap-3 border-b border-line px-6 py-5">
            <div className="min-w-0 flex-1">
              <h2 id={titleId} className="truncate text-xl font-semibold text-ink">
                {title}
              </h2>
              {description ? (
                <p id={descriptionId} className="mt-0.5 text-base text-ink-muted">
                  {description}
                </p>
              ) : null}
            </div>
            {headerStatus !== undefined ? <div className="flex shrink-0 items-center self-center">{headerStatus}</div> : null}
            <Button
              variant="ghost"
              size="md"
              iconOnly
              onClick={onClose}
              aria-label="Close"
              className="group -mr-2 -mt-1 shrink-0"
            >
              <IconClose className="!h-5 !w-5 text-ink-faint group-hover:text-ink-subtle" />
            </Button>
          </div>
        )}
        <div data-overlay-body="" className="min-h-0 flex-1 overflow-y-auto p-6">
          {children}
        </div>
        {/* Kept mounted while the panel wants a status at all, so the change of
            words is what a screen reader announces. */}
        {status !== undefined ? (
          <p
            className={cn('px-6 text-sm text-ink-muted', status ? 'border-t border-line py-2' : 'h-0 overflow-hidden')}
            role="status"
            aria-live="polite"
          >
            {status}
          </p>
        ) : null}
        {footer || footerStart ? (
          <div
            data-overlay-footer=""
            className="flex min-h-16 shrink-0 flex-wrap items-center gap-3 border-t border-line px-6 py-3"
          >
            {footerStart ? <div className="flex items-center gap-3">{footerStart}</div> : null}
            <div className="ml-auto flex flex-wrap items-center justify-end gap-3">{footer}</div>
          </div>
        ) : null}
      </div>
    </div>
  )
}

/**
 * Focus for a panel that does not trap it.
 *
 * A modal panel moves focus in and gives it back through its focus trap. A
 * non-modal one had neither, so opening a block's settings from the keyboard
 * left focus on the page body with the panel some forty Tab presses away, and
 * Done dropped it there again. So focus moves to the panel when it opens and
 * goes back when it closes, but only when it is still in the panel or lost to
 * the body: somebody who clicked on something else has put it where they want.
 */
function useNonModalFocus(
  active: boolean,
  containerRef: RefObject<HTMLDivElement>,
  returnFocus: (() => HTMLElement | null) | undefined,
): void {
  const back = useRef(returnFocus)
  back.current = returnFocus
  useEffect(() => {
    if (!active) return
    const container = containerRef.current
    const before = document.activeElement as HTMLElement | null
    container?.focus({ preventScroll: true })
    return () => {
      const current = document.activeElement
      const lost = current === null || current === document.body || (container !== null && container.contains(current))
      if (!lost) return
      const target = back.current?.() ?? (before?.isConnected ? before : null)
      target?.focus({ preventScroll: true })
    }
  }, [active, containerRef])
}
